import {
  Anchor,
  Box,
  Container,
  Divider,
  Heading,
  Rhythm,
  Text,
  TextSmall,
} from '@trmediaab/zebra';
import type {
  Campaign,
  CheckoutSite,
  Product,
  SiteOffers,
} from '@trmediaab/zebra-types/trhub';
import { useCallback } from 'react';

import ProductsList from './ProductsList';

interface ProductsPageProps {
  offersData: SiteOffers;
  authUrl: string;
  registerUrl: string;
  isAuthenticated: boolean;
  site: CheckoutSite;
  isGiftPage: boolean;
  onProductSelected: (productSku: Product['sku'], product: Product) => void;
  onCampaignSelected: (campaignId: Campaign['_id']) => void;
}

const ProductsPage = ({
  offersData,
  authUrl,
  registerUrl,
  isAuthenticated,
  site,
  isGiftPage = false,
  onProductSelected,
  onCampaignSelected,
}: ProductsPageProps) => {
  const handleCampaignSelected = useCallback(
    (campaign: Campaign) => {
      onCampaignSelected(campaign._id);
    },
    [onCampaignSelected],
  );

  const handleProductSelected = useCallback(
    (product: Product) => {
      onProductSelected(product.sku, product);
    },
    [onProductSelected],
  );

  const { products, campaigns, headline, preamble } = offersData;

  // Should not happen but if something went wrong downstream or if
  // no products or campaigns has been promoted in admin we'll get nothing.
  if (products.length === 0 && campaigns.length === 0) {
    return <Text>Ett fel har inträffat. Var god försök igen senare.</Text>;
  }

  const isTravronden = ['travronden', 'travronden_spel'].includes(site);

  const showAuthenticationHelp = isTravronden && !isGiftPage;

  return (
    <>
      <Box variant="offersPageHeader">
        <Container variant="offersPage">
          <Heading.h1 variant="offersPageHeader">{headline}</Heading.h1>
          <Text.p variant="offersPageHeader">{preamble}</Text.p>
        </Container>
      </Box>

      <Container
        variant="offersPage"
        maxWidth="1400px"
        mt={[4, null, '80px']}
        mb={[4, null, 5]}
      >
        <ProductsList
          products={products}
          campaigns={campaigns}
          onProductSelected={handleProductSelected}
          onCampaignSelected={handleCampaignSelected}
        />

        <Text maxWidth="640px" mt={[4, null, 5]} mx="auto" textAlign="center">
          <Rhythm between={{ _: 3, md: 4 }}>
            <Divider width="60px" mx="auto" />

            <Box>
              <Text fontWeight="medium" fontSize="3">
                Hur kan vi hjälpa dig?
              </Text>
              <TextSmall display="block" color="text.fadedDarker">
                Kontakta kundtjänst på TR Media – svar inom 48 timmar.
              </TextSmall>
            </Box>

            {showAuthenticationHelp && isAuthenticated === false && (
              <Box>
                <Text.strong>Är du redan prenumerant?</Text.strong>
                <Text>
                  <Anchor href={registerUrl}>
                    Aktivera ditt konto och få full tillgång
                  </Anchor>
                </Text>
              </Box>
            )}

            {showAuthenticationHelp && isAuthenticated && (
              <Box>
                <Text.strong>
                  Är du redan prenumerant men saknar tillgång?
                </Text.strong>
                <Text>
                  <Anchor
                    rel="noopener noreferrer"
                    target="_blank"
                    href="mailto:info@trmedia.se"
                  >
                    Kontakta kundtjänst för att aktivera ditt konto
                  </Anchor>
                </Text>
              </Box>
            )}

            <Box>
              <Text.strong>Är det något som strular?</Text.strong>
              <Text>
                <Anchor
                  rel="noopener noreferrer"
                  target="_blank"
                  href="mailto:info@trmedia.se"
                >
                  Kontakta kundtjänst på info@trmedia.se
                </Anchor>
              </Text>
            </Box>

            {!isGiftPage && isAuthenticated === false && (
              <Box>
                <Text.strong>Har du redan ett konto?</Text.strong>
                <Text>
                  <Anchor href={authUrl}>Logga in här</Anchor>
                </Text>
              </Box>
            )}
          </Rhythm>
        </Text>
      </Container>
    </>
  );
};

export default ProductsPage;
